import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button, Input, message, Modal, Trans } from '@/components'

import { validFileName } from '../fs-operations'
import type { IFile } from '../types'

interface CreateFolderBaseProps {
  dir: string
  files?: IFile[]
  onCreate: (folderName: string) => Promise<void>
  onOk?: () => void
  disabled?: boolean
}

export default function CreateFolderBase({
  files,
  onCreate,
  onOk,
  disabled = false,
}: CreateFolderBaseProps) {
  const { t } = useTranslation()
  const [visible, setVisible] = useState(false)
  const [folderName, setFolderName] = useState('')
  const [loading, setLoading] = useState(false)

  const isNameOK = (name: string) => {
    if (files?.some((it: any) => it.name == name) || !validFileName(name)) {
      return false
    }
    return true
  }

  const onConfirmClick = async () => {
    setLoading(true)
    try {
      await onCreate(folderName)
      message.success(t('data.newFolderModal.success'))
      setVisible(false)
      setFolderName('')
      onOk?.()
    } catch (error) {
      message.error(t('data.newFolderModal.error'))
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !loading && isNameOK(folderName)) {
      e.preventDefault()
      onConfirmClick()
    }
  }

  return (
    <>
      <Button onClick={() => setVisible(true)} disabled={disabled}>
        <Trans i18nKey="data.newFolderBtn">New Folder</Trans>
      </Button>
      <Modal open={visible} onClose={() => setVisible(false)}>
        <Modal.Header>{t('data.newFolderModal.title')}</Modal.Header>
        <Modal.Body>
          <Input
            placeholder={t('data.newFolderModal.placeholder')}
            value={folderName}
            maxLength={30}
            disabled={loading}
            onChange={(e) => setFolderName(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
          />
        </Modal.Body>
        <Modal.Footer>
          <Button type="outline" onClick={() => setVisible(false)}>
            <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
          </Button>
          <Button
            type="secondary"
            onClick={onConfirmClick}
            disabled={!isNameOK(folderName) || loading}
          >
            <Trans i18nKey="common.confirmModalOk">Confirm</Trans>
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
